import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, ArrowRight, LogIn } from "lucide-react";
import { useOrganizerAuth } from "../auth/OrganizerAuthContext";
import PasswordInput from "../components/PasswordInput";

export default function OrganizerLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useOrganizerAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      const next = params.get("next") || "/organizer/dashboard";
      navigate(next, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Đăng nhập thất bại.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center px-4 py-12" data-testid="organizer-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-espresso-soft hover:text-espresso text-sm">
          <Sparkles className="w-4 h-4" />
          <span className="tracking-[0.28em] uppercase">Nail Viet Club</span>
        </Link>

        <h1 className="mt-6 text-4xl font-serif text-espresso">Đăng nhập tổ chức</h1>
        <p className="mt-2 text-espresso-soft">Vào dashboard để quản lý sự kiện của bạn.</p>

        <form onSubmit={submit} className="mt-8 bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-espresso/10 space-y-5">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">Email</span>
            <input
              type="email"
              data-testid="org-login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 w-full bg-linen-100/60 border border-espresso/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">Mật khẩu</span>
            <PasswordInput
              data-testid="org-login-password"
              testIdSuffix="org-login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              wrapperClassName="mt-1"
              leftIcon={null}
              className="w-full bg-linen-100/60 border border-espresso/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso"
            />
          </label>

          {error && (
            <div data-testid="org-login-error" className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="org-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full py-3 font-semibold tracking-wide disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            {busy ? "Đang đăng nhập…" : "Đăng nhập"}
            {!busy && <ArrowRight className="w-4 h-4" />}
          </button>

          <div className="flex items-center justify-between text-sm pt-1">
            <Link
              to="/organizer/forgot-password"
              data-testid="org-login-forgot"
              className="text-espresso-soft hover:text-espresso underline-offset-2 hover:underline"
            >
              Quên mật khẩu?
            </Link>
            <Link
              to="/organizer/signup"
              className="text-espresso-soft hover:text-espresso underline-offset-2 hover:underline"
            >
              Tạo tài khoản
            </Link>
          </div>

          <p className="text-center text-sm text-espresso-soft">
            Chưa có tài khoản?{" "}
            <Link to="/organizer/signup" className="text-espresso font-medium underline">Đăng ký miễn phí</Link>
          </p>
          {/* Phase 8.19 — Safety net: master admins land here occasionally
              because it's the top-right button. Give them a quiet way out. */}
          <p className="text-center text-[11px] text-espresso-soft/70 pt-2">
            Bạn là quản trị viên?{" "}
            <Link
              to="/admin/login"
              data-testid="org-login-admin-link"
              className="underline hover:text-espresso"
            >
              Đăng nhập quản trị
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
